import {getAssetUrl} from '@Utils/s3.util';
import type {Component, PropertyProps} from 'grapesjs';
import type {PrimaryConfigOptions} from '@/email-maker/grapesjs-email-editor-plugin/src';

export interface UploadButtonProps extends PropertyProps {
  component?: Component;
  label: string;
  onClick: (callback: (url: string) => void) => void;
  onClear: () => void;
}

export default (opt: PrimaryConfigOptions): void => {
  const {editor} = opt;
  const styleManager = editor.StyleManager;

  /**
   * Upload button component
   */
  styleManager.addType(window.PMW.emails.styles.ID_UPLOAD_BUTTON, {
    create(data) {
      const {change} = data;
      const props = data.props as UploadButtonProps;
      props.component = props.component ?? editor.getSelected();
      const labelContainer = getLabelContainer(props);
      attachButtonHandlers(props, change, labelContainer);

      return labelContainer;
    },

    emit(data, eventData: {labelContainer: HTMLElement; url: string; showDelete: boolean}) {
      const props = data.props as UploadButtonProps;
      const {labelContainer, url, showDelete} = eventData;
      updateLabelStylesOnUpload(props, labelContainer, url, showDelete);
    },
  });

  /**
   * Show/hide delete button and stop icon on label
   */
  function updateLabelStylesOnUpload(_props: UploadButtonProps, labelContainer: HTMLElement, previewURL = '', showDelete: boolean): void {
    const deleteBtn = labelContainer.querySelector('.js-upload-delete-btn');
    const labelPreview = labelContainer.querySelector('.js-upload-preview');

    if (labelPreview) {
      (labelPreview as HTMLElement).style.backgroundImage = `url("${previewURL || ''}")`;
    }

    if (deleteBtn) {
      if (showDelete) {
        deleteBtn.classList.remove('_hidden');
      } else {
        deleteBtn.classList.add('_hidden');
      }
    }
  }

  /**
   * Creates the label container element
   */
  function getLabelContainer(props: UploadButtonProps): HTMLDivElement {
    const labelContainer = document.createElement('div');

    if (!props.component) {
      throw new Error('No component available for upload button');
    }

    const style = props.component.getStyle() || {};
    const defaultPreviewURL = getAssetUrl('email-editor-upload-media-empty-placeholder.png');

    let previewURL = '';
    if (props.property && typeof style[props.property] === 'string') {
      previewURL = (style[props.property] as string).replace(/"/g, '');
    }

    labelContainer.className = props.type ?? '';
    labelContainer.innerHTML = `<div class="flex-justify-between _full-width">
                <span class="style-manager-label">${props.label}</span>
                <div class="flex-row-align-center">
                    <button class="btn clear-btn js-upload-delete-btn bg-neutral-0 border-s-darker radius-4 _cursor-pointer spacing-p-0 ${previewURL ? '' : '_hidden'}">
                        <i class="icon icon-delete body-m"></i>
                    </button>
                    <button class="btn js-upload-label-btn flex-center bg-neutral-0 border-s-dark _cursor-pointer radius-8 spacing-p-1 hover-transition-all">
                        <div class="upload-preview radius-4 spacing-m-r-1 js-upload-preview" style="background-image: ${previewURL ? `url(${previewURL})` : `url(${defaultPreviewURL})`};"></div>
                        <i class="pmw-icon icon-pencil content-body size-icon-16 js-upload-label-icon"></i>
                    </button>
                </div>
            </div>`;

    return labelContainer;
  }

  /**
   * Attaches event handlers to upload and delete buttons
   */
  function attachButtonHandlers(
    props: UploadButtonProps,
    change: (data: {labelContainer: HTMLElement; url: string; showDelete: boolean}) => void,
    labelContainer: HTMLElement
  ): void {
    const uploadBtn = labelContainer.querySelector('.js-upload-label-btn');
    const deleteBtn = labelContainer.querySelector('.js-upload-delete-btn');

    if (uploadBtn) {
      uploadBtn.addEventListener('click', function () {
        // Handle the async operation properly
        void (async (): Promise<void> => {
          const previewURL = await new Promise<string>(resolve => {
            if (typeof props.onClick === 'function') {
              props.onClick(resolve);
            }
          });

          change({labelContainer, url: previewURL, showDelete: true});
        })();
      });
    }

    if (deleteBtn) {
      deleteBtn.addEventListener('click', function () {
        if (typeof props.onClear === 'function') {
          props.onClear();
        }

        change({labelContainer, url: getAssetUrl('email-editor-upload-media-empty-placeholder.png'), showDelete: false});
      });
    }
  }
};